import { useEffect, useState } from 'react';
import { X, Calendar, ChevronLeft, ChevronRight, Sparkles, MapPin } from 'lucide-react';
import { FarmEvent, getImageStyleFromUrl } from '../types';

interface EventDetailModalProps {
  event: FarmEvent | null;
  onClose: () => void;
}

export default function EventDetailModal({ event, onClose }: EventDetailModalProps) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    if (!event) return;
    setActiveImageIndex(0);

    // Stop page scroll behind modal
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [event]);

  if (!event) return null;

  // Compile full list of event images
  const images = event.galleryUrls && event.galleryUrls.length > 0 
    ? event.galleryUrls 
    : [event.thumbnailUrl];

  const handlePrevImage = () => {
    setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setActiveImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div
      id="event-detail-backdrop"
      className="fixed inset-0 z-50 bg-stone-950/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[#FDFCF9] text-[#1A241F] w-full max-w-4xl rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-[#E6E1D4] flex flex-col md:flex-row max-h-[90vh]">
        {/* Left Side: Media Carousel */}
        <div className="relative w-full md:w-1/2 bg-[#0E1E16] flex items-center justify-center group h-[280px] sm:h-[360px] md:h-auto">
          <img
            src={images[activeImageIndex]}
            alt={event.title}
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            style={getImageStyleFromUrl(images[activeImageIndex])}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />

          {/* Nav arrows (only if multiple images exist) */}
          {images.length > 1 && (
            <>
              <button
                onClick={handlePrevImage}
                className="absolute left-3 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white border border-white/10 transition-all cursor-pointer"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white border border-white/10 transition-all cursor-pointer"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}

          {/* Indicator count badge */}
          <div className="absolute bottom-4 left-4 bg-black/60 border border-white/10 backdrop-blur-md px-3.5 py-1 rounded-full text-white text-xs font-medium">
            Photo {activeImageIndex + 1} of {images.length}
          </div>
        </div>

        {/* Right Side: Narrative Details */}
        <div className="w-full md:w-1/2 p-6 sm:p-8 flex flex-col justify-between overflow-y-auto max-h-[48vh] md:max-h-[90vh]">
          <div>
            {/* Header / Dismiss */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <span className={event.category === 'upcoming' ? 'estate-chip' : 'estate-chip-amber'}>
                  <Sparkles size={11} />
                  {event.category === 'upcoming' ? 'Upcoming Gathering' : 'Past Tradition'}
                </span>
                {event.type && (
                  <span className="text-xs text-[#65756C] font-medium">
                    • {event.type}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#65756C] hover:text-[#14532D] hover:bg-[#F0FDF4] rounded-full transition-colors cursor-pointer border border-[#E6E1D4]"
                title="Dismiss"
              >
                <span>Close</span>
                <X size={14} />
              </button>
            </div>

            {/* Event Title */}
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1A241F] leading-snug mb-3">
              {event.title}
            </h2>

            {/* Event Date badge */}
            <div className="flex items-center gap-2 text-sm text-[#65756C] mb-5 font-medium">
              <Calendar size={15} className="text-[#14532D]" />
              <span>{event.date}</span>
              <span className="text-stone-300">•</span>
              <span className="inline-flex items-center gap-1 text-xs text-[#65756C]">
                <MapPin size={12} className="text-amber-600" />
                Mithran Farm House, Sirudavoor
              </span>
            </div>

            {/* Event Narrative */}
            <div className="space-y-4 text-sm text-[#37463E] leading-relaxed mb-6">
              <div className="bg-[#FAF7F0] p-4.5 rounded-2xl border-l-4 border-[#14532D]">
                <p className="font-serif italic text-base text-[#24332B] leading-relaxed">
                  "{event.description}"
                </p>
              </div>

              {event.longDescription && (
                <div className="pt-2 text-stone-700 leading-relaxed space-y-3 font-normal">
                  {event.longDescription.split('\n\n').map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sub-gallery thumbnail indicators */}
          {images.length > 1 && (
            <div className="mt-6 border-t border-[#E6E1D4] pt-4">
              <span className="text-[10px] text-[#65756C] font-semibold uppercase tracking-wider block mb-2.5">
                Event Sub-Gallery
              </span>
              <div className="flex space-x-2 overflow-x-auto pb-1">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`relative w-14 h-14 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 cursor-pointer ${
                      activeImageIndex === idx 
                        ? 'border-[#14532D] ring-2 ring-emerald-500/30 scale-105' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} className="w-full h-full object-cover" style={getImageStyleFromUrl(img)} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
